import Graphic from '@arcgis/core/Graphic';
import type WebMap from '@arcgis/core/WebMap';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Feature from '@arcgis/core/widgets/Feature.js';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import { isNil } from 'ramda';
import React, { useEffect, useState } from 'react';

import { useMountFeatureDetails } from '@/features/build-calculation-context/components/Home/components/SelectedFeaturePanel/SelectedFeaturePanel';
import type { DomainGuid } from '@/features/execute-calculation-set/overmind/calculation';
import { useAppState } from '@/overmind';
import {
  getBasemapSpatialReference,
  getWebMap
} from '@/overmind/effects/adapters/ArcgisMapAdapter/getWebMapTitles';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

type WebMapLoading =
  | {
      type: 'NOT_READY';
    }
  | {
      type: 'LOADING';
    }
  | {
      type: 'READY';
      webMap: WebMap;
    };

export const useWebMap = () => {
  const arcgisConfiguration = useAppState().arcgis.config;

  const [webMap, setWebMap] = useState<WebMapLoading>({ type: 'NOT_READY' });

  React.useEffect(() => {
    if (
      webMap.type === 'READY' ||
      arcgisConfiguration.current !== 'LOADED' ||
      !arcgisConfiguration.value.map.webMap
    )
      return;
    setWebMap({ type: 'LOADING' });
    const unloadedWebMap = getWebMap(arcgisConfiguration.value.map.webMap);

    unloadedWebMap.load().then(webMap => {
      setWebMap({ type: 'READY', webMap });
    });
  }, [arcgisConfiguration]);

  return webMap;
};

type GridElementRelatedFeatureLayer =
  | {
      type: 'NOT_LOADED';
    }
  | { type: 'LOADING' }
  | {
      type: 'LOADED';
      layer: __esri.FeatureLayer;
    }
  | {
      type: 'NOT_AVAILABLE';
      reason: string;
    };

const useFeatureLayerFromWebMapForFeature = (
  layerId?: number,
  objectId?: DomainGuid
) => {
  const [featureLayer, setFeatureLayer] =
    useState<GridElementRelatedFeatureLayer>({ type: 'NOT_LOADED' });

  const [allAvailableFeatureLayersLoaded, setAllAvailableFeaturesLoaded] =
    useState(false);

  const webMap = useWebMap();
  React.useEffect(() => {
    if (featureLayer.type === 'NOT_AVAILABLE') return;

    if (
      featureLayer.type === 'LOADED' ||
      allAvailableFeatureLayersLoaded === true ||
      webMap.type !== 'READY'
    )
      return;

    if (isNil(layerId) || isNil(objectId)) {
      setFeatureLayer({
        type: 'NOT_AVAILABLE',
        reason: `Either layerId (${layerId}) or objectId (${objectId}) are not available.`
      });
      return;
    }

    queryGridElementsDetailsByGlobalID(
      webMap.webMap.allLayers
        .map(layer => ({
          globalId: objectId,
          layer
        }))
        .toArray()
    ).then(matchingFeatures => {
      setAllAvailableFeaturesLoaded(true);
      const matchingLayers = matchingFeatures.filter(
        feature => feature.features.length > 0
      );
      if (matchingLayers.length === 0) {
        setFeatureLayer({
          type: 'NOT_AVAILABLE',
          reason: 'No matching layers found. Please check webmap configuration.'
        });
      }
      if (matchingLayers.length > 1) {
        setFeatureLayer({
          type: 'NOT_AVAILABLE',
          reason:
            'At least two matching layers found. Please check configuration of datasources.'
        });
      }
      if (matchingLayers.length === 1) {
        setFeatureLayer({
          type: 'LOADED',
          layer: matchingLayers[0].features[0].layer as __esri.FeatureLayer
        });
      }
    });
  }, [featureLayer, webMap, objectId, layerId]);

  React.useEffect(() => {
    setFeatureLayer({ type: 'LOADING' });
  }, [webMap]);

  React.useEffect(() => {
    setFeatureLayer({ type: 'NOT_LOADED' });
    setAllAvailableFeaturesLoaded(false);
  }, [layerId]);

  return featureLayer;
};

export type FeatureDetailsState =
  | 'NOT_LOADED'
  | 'LOADING'
  | 'LOADED'
  | 'CANNOT_BE_LOADED';

export function useFeatureDetails({
  of: objectId,
  displayIn: detailsContainerRef
}: {
  of: `{${string}}`;
  displayIn: React.RefObject<HTMLDivElement>;
}) {
  const features = useAppState().arcgis.featureGeometries;
  const [title, setTitle] = useState<string | undefined>(undefined);
  const webMap = useWebMap();

  const [featureDetailsLoadingStatus, setFeatureDetailsLoadingStatus] =
    React.useState<FeatureDetailsState>('NOT_LOADED');
  const layer = features.value?.find(layer => {
    const f = layer.features.find(feature => feature.gc.globalId === objectId);
    return f;
  });

  if (isNil(layer)) return;

  const selectedGridElement = layer.features.find(
    feature => feature.gc.globalId === objectId
  );

  if (!selectedGridElement) return;
  const featureLayer = useFeatureLayerFromWebMapForFeature(
    layer.layerId,
    selectedGridElement.gc.globalId
  );
  const [feature, selectFeature] = React.useState<
    FeatureWithIDs<unknown> | undefined
  >(undefined);
  useMountFeatureDetails({
    detailsOf: feature,
    mountTo: detailsContainerRef as any
  });
  useEffect(() => {
    setTitle(undefined);
    selectFeature(undefined);
    setFeatureDetailsLoadingStatus('NOT_LOADED');
  }, [objectId, featureLayer]);
  useEffect(() => {
    if (webMap.type !== 'READY') return;
    if (featureLayer.type === 'LOADING') {
      setFeatureDetailsLoadingStatus('LOADING');
      return;
    }
    if (featureLayer.type === 'NOT_AVAILABLE') {
      setFeatureDetailsLoadingStatus('CANNOT_BE_LOADED');
      return;
    }
    if (featureLayer.type !== 'LOADED') return;
    if (feature) return;
    const layer = features.value?.find(layer => {
      const f = layer.features.find(
        feature => feature.gc.globalId === objectId
      );
      return f;
    });

    if (isNil(layer)) return;

    const selectedGridElement = layer.features.find(
      feature => feature.gc.globalId === objectId
    );

    if (!selectedGridElement) return;
    const arcgisGeometry = geojsonToArcGIS(selectedGridElement) as any;
    if (selectedGridElement.id === undefined || !featureLayer) return;

    if (featureDetailsLoadingStatus === 'LOADING') return;
    setFeatureDetailsLoadingStatus('LOADING');
    featureLayer.layer
      .queryFeatures({
        objectIds: [Number(selectedGridElement.id)],
        spatialRelationship: 'intersects',
        returnGeometry: true,
        outFields: ['*'],
        where: '1=1'
      })
      .then(res => {
        const graphic = new Graphic({
          attributes: arcgisGeometry.attributes,
          geometry: arcgisGeometry.geometry,
          layer: featureLayer.layer,
          popupTemplate:
            featureLayer?.layer?.popupTemplate ||
            featureLayer?.layer?.defaultPopupTemplate
        });
        if (!feature) {
          const basemapSpatialReference = getBasemapSpatialReference(
            webMap.webMap
          );
          if (basemapSpatialReference) {
            displayFeatureDetailsBy(
              webMap.webMap,
              basemapSpatialReference,
              graphic,
              {
                withCleanup: false,
                onTitleAvailable: title => {
                  setTitle(title);
                }
              }
            );
          }
          selectFeature(selectedGridElement);
          setFeatureDetailsLoadingStatus('LOADED');
        }
      });
  }, [feature, featureLayer, webMap]);

  return { status: featureDetailsLoadingStatus, title };
}

export function useFeatureDetailsV2({
  of: objectId,
  displayIn: detailsContainerRef
}: {
  of: `{${string}}`;
  displayIn: React.RefObject<HTMLDivElement>;
}) {
  const webMap = useWebMap();
  const featureFromOvermind = useAppState().selectedFeature.current;
  const [title, setTitle] = useState<string | undefined>(undefined);

  const [featureDetailsLoadingStatus, setFeatureDetailsLoadingStatus] =
    React.useState<FeatureDetailsState>('NOT_LOADED');
  const layerId = featureFromOvermind?.gc.layerId;
  const selectedGridElement = featureFromOvermind;

  const [feature, selectFeature] = React.useState<
    FeatureWithIDs<unknown> | undefined
  >(undefined);

  const featureLayer = useFeatureLayerFromWebMapForFeature(
    featureFromOvermind?.gc.layerId,
    featureFromOvermind?.gc.globalId
  );

  useMountFeatureDetails({
    detailsOf: feature,
    mountTo: detailsContainerRef as any
  });

  useEffect(() => {
    setTitle(undefined);
    selectFeature(undefined);
    setFeatureDetailsLoadingStatus('NOT_LOADED');
  }, [objectId, featureLayer]);

  useEffect(() => {
    if (webMap.type !== 'READY') return;
    if (!featureLayer) return;
    if (featureLayer.type === 'LOADING') {
      setFeatureDetailsLoadingStatus('LOADING');
      return;
    }
    if (featureLayer.type === 'NOT_AVAILABLE') {
      setFeatureDetailsLoadingStatus('CANNOT_BE_LOADED');
      return;
    }
    if (featureLayer.type !== 'LOADED') return;
    if (feature) return;
    if (isNil(layerId)) return;
    if (!selectedGridElement) return;
    const arcgisGeometry = geojsonToArcGIS(selectedGridElement) as any;
    if (selectedGridElement.gc.objectId === undefined || !featureLayer) return;
    if (featureDetailsLoadingStatus === 'LOADING') return;

    setFeatureDetailsLoadingStatus('LOADING');
    featureLayer.layer
      .queryFeatures({
        objectIds: [Number(selectedGridElement.gc.objectId)],
        spatialRelationship: 'intersects',
        returnGeometry: true,
        outFields: ['*'],
        where: '1=1'
      })
      .then(res => {
        const graphic = new Graphic({
          attributes: arcgisGeometry.attributes,
          geometry: arcgisGeometry.geometry,
          layer: featureLayer.layer,
          popupTemplate:
            featureLayer?.layer?.popupTemplate ||
            featureLayer?.layer?.defaultPopupTemplate
        });
        if (!feature) {
          const basemapSpatialReference = getBasemapSpatialReference(
            webMap.webMap
          );
          if (basemapSpatialReference) {
            displayFeatureDetailsBy(
              webMap.webMap,
              basemapSpatialReference,
              graphic,
              {
                withCleanup: false,
                onTitleAvailable: title => {
                  setTitle(title);
                }
              }
            );
          }
          selectFeature(selectedGridElement);
          setFeatureDetailsLoadingStatus('LOADED');
        }
      });
  }, [feature, featureLayer, webMap, layerId, selectedGridElement]);

  return { status: featureDetailsLoadingStatus, title };
}

export function queryGridElementsDetails(
  response: NearbyObjectsWithRelatedLayers.ByObjectId
) {
  return Promise.allSettled(_queryGridElementsDetails(response)).then(
    takeOnlyCompleted
  );
}
function _queryGridElementsDetails(
  response: NearbyObjectsWithRelatedLayers.ByObjectId
) {
  const features = takeQueryableLayers(response);
  return features.map(result =>
    asFeatureLayer(result.layer).queryFeatures({
      objectIds: [(result.objectid as any).asNumber()],
      spatialRelationship: 'intersects',
      returnGeometry: true,
      outFields: ['*'],
      outSpatialReference: { wkid: 4326 },
      where: '1=1'
    })
  );

  function asFeatureLayer(layer: QueryableLayer) {
    const featureLayer = layer as unknown as __esri.FeatureLayer;
    return featureLayer;
  }
}

export class ObjectId {
  private constructor(private value: number) {}

  static fromNumber(value: number) {
    return new ObjectId(value);
  }

  asNumber() {
    return this.value;
  }
}

export declare module NearbyObjectsWithRelatedLayers {
  type ByObjectId = { objectid: ObjectId; layer: AnyLayer }[];
  type ByGlobalId = { globalId: DomainGuid; layer: AnyLayer }[];
}

export function queryGridElementsDetailsByGlobalID(
  objects: NearbyObjectsWithRelatedLayers.ByGlobalId
) {
  return Promise.allSettled(_queryGridElementsDetailsByGlobalID(objects)).then(
    takeOnlyCompleted
  );

  function _queryGridElementsDetailsByGlobalID(
    objects: NearbyObjectsWithRelatedLayers.ByGlobalId
  ) {
    const features = takeQueryableLayers(objects);
    return features.map(result =>
      asFeatureLayer(result.layer).queryFeatures({
        where: `globalid = '${result.globalId}'`,
        spatialRelationship: 'intersects',
        returnGeometry: true,
        outFields: ['*']
      })
    );

    function asFeatureLayer(layer: QueryableLayer) {
      const featureLayer = layer as unknown as __esri.FeatureLayer;
      return featureLayer;
    }
  }
}

export type AnyLayer = { type: string } & Omit<__esri.Layer, 'type'>; // we want to transform AnyLayer, regardless of its type, to a QueryableLayer, hence type is any string
export type QueryableLayer = __esri.Layer | __esri.SubtypeSublayer;

type TakeQueryableLayers = (
  layers: { layer: AnyLayer }[]
) => { layer: QueryableLayer; objectid: number; globalId: DomainGuid }[];
type LayerWithFeatureIdentifiers = ReturnType<TakeQueryableLayers>[number];

const takeQueryableLayers: TakeQueryableLayers = response => {
  return response
    .filter(object => object.layer)
    .filter(
      object =>
        object.layer.type === 'feature' ||
        object.layer.type === 'subtype-sublayer' ||
        object.layer.type === 'subtype-group'
    )
    .map(object => object as unknown as LayerWithFeatureIdentifiers);
};

export function takeOnlyCompleted(
  featureDetails: PromiseSettledResult<__esri.FeatureSet>[]
) {
  return featureDetails
    .filter(resolvedPromise => resolvedPromise.status === 'fulfilled')
    .flatMap(
      resolvedPromise =>
        (
          resolvedPromise as unknown as PromiseFulfilledResult<__esri.FeatureSet>
        ).value
    );
}

export function displayFeatureDetailsBy(
  map: __esri.Map,
  spatialReference: __esri.SpatialReference,
  selectedFeatureGraphic: __esri.Graphic,
  options: {
    withCleanup?: boolean;
    onTitleAvailable?: (title: string | undefined) => void;
  } = { withCleanup: true }
) {
  ensureGraphicPopupTemplate(selectedFeatureGraphic);
  if (options.withCleanup) {
    const existingFeatureDiv = document.querySelector('#feature');
    if (existingFeatureDiv) {
      existingFeatureDiv.remove();
    }
  }
  const div = document.createElement('div');

  div.setAttribute('id', 'feature');
  div.className = 'dF fdC ofA';
  div.style.maxHeight = '20rem';
  if (!document.querySelector('#feature')) {
    document.body.appendChild(div);
  }
  const feature = new Feature({
    graphic: selectedFeatureGraphic,
    map,
    spatialReference,
    container: 'feature',
    visibleElements: {
      title: false
    }
  });

  void (async () => {
    await reactiveUtils.once(() => feature.title);
    options.onTitleAvailable?.(feature.title);
  })();

  // feature.renderNow();
}

function ensureGraphicPopupTemplate(selectedFeatureGraphic: any) {
  if (selectedFeatureGraphic.popupTemplate) return;
  if (!selectedFeatureGraphic.layer) return;

  selectedFeatureGraphic.popupTemplate =
    selectedFeatureGraphic.layer.popupTemplate ||
    selectedFeatureGraphic.layer.defaultPopupTemplate;
}
